import { z } from 'zod';

const formDefinitionSectionBase = z.object({
  name: z.string().trim().min(3),
  label: z.string(),
  title: z.string(),
  subtitle: z.string().optional(),
  description: z.string().optional(),
  footnote: z.string(),
}).describe('Base attributes that every section type contains');

/*
 * Table type section schemas
 */

export enum TableCellType {
  Text = 'text',
  LabelText = 'labelText',
  Textarea = 'textarea',
  Select = 'select',
}

export enum TableCellDataFormat {
  FixedText = 'fixedText',
  Percentage = 'percentage',
  PercentageRange = 'percentageRange',
}

const rowCell = z.discriminatedUnion('type', [
  z.object({
    type: z.enum([TableCellType.Text]),
    dataFormat: z.enum(TableCellDataFormat).optional(),
    placeholder: z.string().optional(),
    pattern: z.string().optional(),
    isCalculated: z.boolean().optional(),
    calculation: z.record(z.string().min(1), z.union([z.string().min(1), z.int().positive(), z.array(z.string().trim().min(1))]))
      .optional()
      .meta({
        type: 'object',
        examples: [
          {
            operation: 'DIVISION',
            numeratorColumn: 'retailSales',
            denominatorColumn: 'pointsOfSales',
            roundingMethod: 'FLOOR',
          },
          {
            operation: 'DIVISION',
            source: 'ROW',
            numeratorRow: 'retailSalesVolume',
            denominatorRow: 'showroomCars',
            roundingMethod: 'ROUND',
          },
          {
            operation: 'PERCENTAGE',
            numeratorColumn: 'salesOutletsInMAR',
            denominatorColumn: 'pointsOfSales',
            roundingMethod: 'TWO_DECIMAL',
          },
          {
            operation: 'PERCENTAGE_WITH_NESTED_SUM',
            source: 'ROW',
            numeratorRowsToSum: ['marSalesAndServiceAutohaus3S', 'marSalesOnly1S'],
            denominatorRowsToSum: ['totalSalesAndServiceAutohaus3S', 'totalSalesOnly1S'],
            roundingMethod: 'ROUND',
          },
          {
            operation: 'SUM',
            rowsToSum: [
              'marSalesAndServiceAutohaus3S',
              'marSalesOnly1S',
              'marServiceOnly2S',
            ],
          },
          {
            operation: 'SUM_FROM_SECTION_ROW',
            sectionName: 'mar20XConversion',
            rowsToSum: ['totalSalesAndServiceAutohaus3S', 'totalServiceOnly2S'],
          },
          {
            operation: 'MULTIPLY',
            rowsToMultiply: ['showroomCars'],
            multiplyByFactor: 80,
          },
          {
            operation: 'COPY_FROM_SECTION_ROW',
            sectionName: 'mar20XConversion',
            rowName: 'totalSalesOutlets3SPlus1S',
          },
          {
            operation: 'PERCENTAGE_WITH_DENOMINATOR_FROM_SECTION_ROW',
            numeratorColumn: 'retailSalesVolume',
            denominatorSectionName: 'salesNetwork',
            denominatorRowName: 'retailSalesVolume',
            roundingMethod: 'TWO_DECIMAL',
          },
        ],
      }),
  }),
  z.object({
    type: z.enum([TableCellType.LabelText]),
    dataFormat: z.enum([TableCellDataFormat.FixedText]),
  }),
  z.object({
    type: z.enum([TableCellType.Textarea]),
    rows: z.int().positive(),
    maxlength: z.int().positive().optional().meta({ maximum: undefined }),
    placeholder: z.string().optional(),
    isCalculated: z.boolean().optional(),
    calculation: z.record(z.string().min(1), z.string().min(1))
      .optional()
      .meta({
        type: 'object',
        examples: [
          {
            operation: 'COPY_FROM_SECTION_ROW_INDEX',
            sectionName: 'networkTransformationStatusTracking',
            rowName: 'outlet',
          },
        ],
      }),
  }),
  z.object({
    type: z.enum([TableCellType.Select]),
    values: z.array(z.string().trim().min(1)),
    placeholder: z.string().optional(),
  }),
]);

const tableSectionData = z.object({
  tableHeaders: z.object({
    style: z.string(),
    font: z.string(),
    headerTexts: z.array(z.object({
      name: z.string(),
      text: z.string(),
      subText: z.string(),
      style: z.string().optional(),
      emptyHeader: z.boolean().optional(),
      disabled: z.boolean().optional(),
      required: z.boolean().optional(),
      hasLeftScrollArrow: z.boolean().optional(),
      canCopyValueFromLastYear: z.boolean().optional(),
      columnHighlightDisabled: z.boolean().optional(),
      isCalculated: z.boolean().optional(),
      tooltipText: z.string().optional(),
    })),
  }),
  rows: z.array(z.object({
    name: z.string().min(3),
    value: z.string().optional(),
    style: z.string(),
    font: z.string(),
    isCalculated: z.boolean().optional(),
    tooltipText: z.string().optional(),
    rowspan: z.int().positive().optional().meta({ maximum: undefined }),
    cells: z.array(rowCell).optional(), // Optional for e. g. dividers.
  })),
});

export enum TableSectionType {
  TableDefault = 'tableDefault',
  TableWide = 'tableWide',
}

export enum InputSectionType {
  SingleTextInput = 'singleTextInput',
  SingleTextareaInput = 'singleTextareaInput',
  PieChartDouble = 'pieChartDouble',
  InfoHeaderText = 'infoHeaderText',
  DealerMarginRemunerationSystem = 'dealerMarginRemunerationSystem',
}

const formDefinitionSectionTable = formDefinitionSectionBase.extend({
  type: z.enum(TableSectionType),
  additionalRowsTable: z.string().min(1).optional(),
  generateRowsFromValues: z.boolean().optional(),
  dependentSection: z.string().min(1).optional(),
  sectionDependencyOn: z.string().min(1).optional(),
  data: tableSectionData,
}).strict();
export type FormDefinitionSectionTable = z.infer<typeof formDefinitionSectionTable>;

/*
 * Info header section schemas
 */

const formDefinitionSectionInfoHeader = formDefinitionSectionBase.extend({
  type: z.enum(['infoHeaderText']),
  text: z.string(),
}).strict();

/*
 * Single text input section schemas
 */

const formDefinitionSectionSingleTextInput = formDefinitionSectionBase.extend({
  type: z.enum([InputSectionType.SingleTextInput]),
  data: z.object({
    inputs: z.array(
      z.object({
        name: z.string().min(1),
        type: z.enum(['text']),
        pattern: z.string().optional(),
        required: z.boolean().optional(),
      }),
    ),
  }),
}).strict();

/*
 * Single textarea input section schemas
 */

const formDefinitionSectionSingleTextareaInput = formDefinitionSectionBase.extend({
  type: z.enum([InputSectionType.SingleTextareaInput]),
  data: z.object({
    inputs: z.array(
      z.object({
        name: z.string().min(1),
        type: z.enum(['textarea']),
        rows: z.int().positive().meta({ maximum: undefined }),
        maxlength: z.int().positive().meta({ maximum: undefined }),
        placeholder: z.string().optional(),
      }),
    ),
  }),
}).strict();

/*
 * Pie chart section schemas
 */

const pieChartInput = z.object({
  name: z.string().min(1),
  type: z.enum(['text']),
  dataFormat: z.enum(['percentage']).optional(),
  placeholder: z.string().optional(),
  pattern: z.string().optional(),
});

const formDefinitionSectionPieChart = formDefinitionSectionBase.extend({
  type: z.enum(['pieChartDouble']),
  data: z.array(
    z.object({
      name: z.string().trim().min(3),
      label: z.string(),
      title: z.string(),
      type: z.enum(['pieChart']),
      footnote: z.string(),
      pieChartInput: z.array(pieChartInput),
      pieChartColors: z.array(z.string().min(1).describe('Color in any HTML-supported format')),
      pieChartButton: z.object({
        text: z.string().min(1),
      }),
    }),
  ),
}).strict();
export type FormDefinitionSectionPieChart = z.infer<typeof formDefinitionSectionPieChart>;

/*
 * Dealer margin section schemas
 */

export enum DealerMarginInputGroupType {
  VariableMarginInputGroup = 'variableMarginInputGroup',
  FixedMarginInputGroup = 'fixedMarginInputGroup',
}

export enum DealerMarginInputType {
  Text = 'text',
  Textarea = 'textarea',
}

export enum DealerMarginInputDataFormat {
  Percentage = 'percentage',
}

const formDefinitionSectionDealerMarginSystem = formDefinitionSectionBase.extend({
  type: z.enum([InputSectionType.DealerMarginRemunerationSystem]),
  visibleToCountries: z.array(z.string().min(2).max(2)).describe('Countries (ISO 3166-1 alpha-2 country code) for which the Dealer Margin section is visible'),
  data: z.array(
    z.object({
      name: z.string().trim().min(3),
      label: z.string().optional(),
      title: z.string().optional(),
      groupedRows: z.array(
        z.object({
          name: z.string().trim().min(3),
          type: z.enum(DealerMarginInputGroupType),
          footnote: z.string().optional(),
          nestedColumnWidth: z.string().optional(),
          inputs: z.array(z.object({
            name: z.string().trim().min(1),
            type: z.enum(DealerMarginInputType),
            label: z.string().optional(),
            style: z.string().optional(),
            dataFormat: z.enum(DealerMarginInputDataFormat).optional(),
            placeholder: z.string().optional(),
            pattern: z.string().optional(),
            rows: z.int().positive().optional().meta({ maximum: undefined }),
            maxlength: z.int().positive().optional().meta({ maximum: undefined }),
            headerText: z.string().optional(),
            columnWidth: z.record(z.string().min(1), z.string().min(1))
              .optional()
              .meta({
                type: 'object',
                examples: [
                  {
                    normalMode: 'col-12',
                    focusMode: 'col-6',
                  },
                  {
                    normalMode: 'col-12',
                    focusMode: 'full-span',
                  },
                ],
              }),
          })),
        }),
      ),
    }),
  ),
}).strict();
export type FormDefinitionSectionDealerMarginSystem = z.infer<typeof formDefinitionSectionDealerMarginSystem>;

// Merge all section types into one final descriminated union type.
export const formDefinitionSection = z.discriminatedUnion('type', [
  formDefinitionSectionInfoHeader,
  formDefinitionSectionDealerMarginSystem,
  formDefinitionSectionPieChart,
  formDefinitionSectionSingleTextareaInput,
  formDefinitionSectionSingleTextInput,
  formDefinitionSectionTable,
]);
